/**
 * Walk-through markers (10 §3): they say "here" and nothing more, so they are small and quiet - a
 * cairn, a scrape under a net, a camp, a culvert mouth. None of them may look like a target.
 */

import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { sided, tubeZ } from './parts';
import { capColor } from './snow';

const TAU = Math.PI * 2;

/**
 * Overlook marker (mission 23): a knee-high cairn of three flat stones and a cap, with a scrap of
 * the player's colour under the top stone - left by whoever reconnoitred the place for him.
 */
export const overlookMarker: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const stone = (w: number, h: number, y: number, tone: string): void => {
    const at = place(rng.range(-0.03, 0.03), y, rng.range(-0.03, 0.03), rng.range(-0.2, 0.2));
    mb.box(w, h, w * 0.9, { all: p.color(tone), top: p.color('rock.hi') }, at);
  };
  stone(0.56, 0.2, 0, 'rock.lo');
  stone(0.44, 0.17, 0.2, 'rock.mid');
  mb.box(0.36, 0.05, 0.34, p.color('playerAccent.0'), place(0, 0.37, 0));
  stone(0.3, 0.14, 0.42, 'rock.mid');
  mb.blob(0.12, 0.09, 0.12, capColor(p, 'rock.hi'), place(0, 0.62, 0), { detail: 0 });
  return mb;
};

/**
 * Observation post (missions 25 and 36): a one-man scrape of turned earth, a scrim net pegged low
 * over its north half, and the spotting scope looking out from under it. The net takes the ground's
 * own ramp - it is camouflage - so only the dark scrape and the scope give the place away.
 */
export const observationPost: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.7, 0.08, 0.66, { all: p.color('dirt.lo'), top: p.color('dirt.mid') });
  // The net slopes up to a ridge stick at the back, one step darker than open ground (which is the ramp's
  // third step in the snow, its second elsewhere): a white net in the arctic. A scrim patch breaks its outline.
  const step = p.biome === 'arctic' ? 2 : 1;
  const net = { all: p.color(`ground.${step - 1}`), top: p.color(`ground.${step}`) };
  mb.box(0.72, 0.05, 0.5, net, place(0, 0.3, -0.1, 0, 1, 0.55));
  const scrim = p.color(p.biome === 'arctic' ? 'ground.3' : 'foliage.mid');
  mb.box(0.3, 0.05, 0.22, scrim, place(-0.12, 0.34, -0.12, 0, 1, 0.55));
  mb.box(0.74, 0.07, 0.07, p.color('timber.lo'), place(0, 0.45, -0.3));
  mb.box(0.1, 0.12, 0.34, p.color('metal.lo'), place(0.14, 0.12, 0.16));
  return mb;
};

/** Where the camp fire is, in the camp's frame: the embers are a separate self-lit part. */
const FIRE = { x: 0.62, z: 0.55 };

/**
 * Partisan camp (mission 31): a lean-to of boughs with its open side to a ring-of-stones fire, and a
 * couple of crates. Seen from the south the lean-to shows its wedge-shaped end - the one silhouette
 * that says "shelter" - and the slope of its roof.
 */
export const partisanCamp: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const slope = Math.atan2(1.12, 1.15);
  mb.with(place(-0.62, 0.66, -0.3, 0, 1, 0, slope), () => {
    const boughs = { all: p.color('foliage.lo'), top: p.color('foliage.mid'), bottom: p.color('ink.1') };
    mb.box(1.62, 0.09, 1.7, boughs, undefined, true);
    if (p.biome === 'arctic') mb.box(0.9, 0.07, 1.1, p.color('ground.3'), place(-0.1, 0.05, 0.1));
  });
  mb.box(0.09, 0.09, 1.9, p.color('timber.lo'), place(-0.04, 1.2, -0.3));
  for (const z of [-1.2, 0.6]) mb.box(0.09, 1.22, 0.09, p.color('timber.lo'), place(-0.04, 0, z));

  // Fire: six stones, a bed of ash, two logs, the embers.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * TAU + rng.range(-0.15, 0.15);
    const at = place(FIRE.x + Math.sin(a) * 0.36, 0, FIRE.z + Math.cos(a) * 0.36, a);
    mb.box(0.2, rng.range(0.12, 0.18), 0.15, { all: p.color('rock.lo'), top: p.color('rock.mid') }, at);
  }
  mb.cylinder(0.3, 0.3, 0.06, 6, p.color('ink.1'), place(FIRE.x, 0, FIRE.z));
  for (const yaw of [0.5, -0.8]) mb.box(0.5, 0.08, 0.09, p.color('timber.lo'), place(FIRE.x, 0.06, FIRE.z, yaw));
  mb.box(0.2, 0.08, 0.16, p.color('fire.1'), place(FIRE.x - 0.03, 0.1, FIRE.z + 0.02, 0.4));
  // Stores, with the radio's place among them.
  const crate = { all: p.color('timber.mid'), top: capColor(p, 'timber.hi') };
  mb.box(0.62, 0.45, 0.5, crate, place(0.72, 0, -0.78, rng.range(-0.2, 0.2)));
  mb.box(0.45, 0.34, 0.4, crate, place(0.25, 0, -1.0, rng.range(-0.3, 0.3)));
  return mb;
};

/** The embers alone, a shade proud of the body's: the self-lit part. */
export const campEmbers: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.24, 0.06, 0.2, p.color('fire.1'), place(FIRE.x - 0.03, 0.14, FIRE.z + 0.02, 0.4));
  mb.box(0.1, 0.05, 0.1, p.color('fire.2'), place(FIRE.x + 0.05, 0.16, FIRE.z - 0.04));
  return mb;
};

/**
 * Culvert (mission 34): the mouth of a pipe under the road - a low concrete headwall with the black
 * bore in it, facing SOUTH, and two wing walls splayed out from its ends. Five parts: the small budget.
 */
export const culvert: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const wall = sided(p, 'concrete', capColor(p, 'concrete.hi'));
  mb.box(2.1, 0.58, 0.3, wall, place(0, 0, -0.3));
  tubeZ(mb, 0, 0.27, -0.4, -0.13, 0.23, 0.23, 6, p.color('concrete.lo'), p.color('ink.0'));
  for (const sx of [-1, 1]) mb.box(0.95, 0.44, 0.22, wall, place(sx * 1.18, 0, 0.08, sx * -0.65));
  return mb;
};
